import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListView, UpdateListViewData } from "@/services/list-view";

interface RenameViewModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    view?: ListView | null;
    onSave: (data: UpdateListViewData) => Promise<void>;
}

export function RenameViewModal({ open, onOpenChange, view, onSave }: RenameViewModalProps) {
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (view) {
            setName(view.name);
        } else {
            setName("");
        }
        setError(null);
    }, [view, open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setError("Name is required");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (view) {
                const data: UpdateListViewData = {
                    name: name.trim()
                };
                await onSave(data);
            }

            onOpenChange(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save view");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Rename View</DialogTitle>
                    <DialogDescription>Give a new name to this list view.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Name
                            </Label>
                            <div className="col-span-3">
                                <Input id="name" value={name} onChange={e => setName(e.target.value)} className="w-full" placeholder="Enter view name" disabled={loading} />
                            </div>
                        </div>
                        {error && <div className="text-sm text-red-600">{error}</div>}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
